import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAsync, useDebounced } from '../lib/useAsync'
import { docPath } from '../lib/types'
import { Chip, EmptyState, Spinner, VisibilityBadge } from '../components/ui'
import { useMeta } from '../lib/seo'

/** Full-page mirror of the ⌘K palette — shareable URLs, tag facets, service scope. */
export function SearchPage() {
  const [params, setParams] = useSearchParams()
  const q = params.get('q') ?? ''
  const tag = params.get('tag') ?? ''
  const service = params.get('service') ?? ''

  const debouncedQ = useDebounced(q, 200)

  // Search result pages are infinite and thin — exactly what crawlers should skip.
  useMeta({ title: 'Search', path: '/search', noindex: true })

  const { data, loading, error } = useAsync(
    (signal) =>
      api.search(
        { q: debouncedQ || undefined, tag: tag || undefined, service: service || undefined },
        signal,
      ),
    [debouncedQ, tag, service],
  )

  const patch = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v)
      else merged.delete(k)
    }
    setParams(merged, { replace: true })
  }

  return (
    <div className="mx-auto max-w-4xl px-1 py-10 lg:px-8">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Search</h1>

      <input
        value={q}
        onChange={(e) => patch({ q: e.target.value })}
        placeholder="Title, description, or tag…"
        autoFocus
        className="mt-5 h-11 w-full rounded-[var(--radius-sh)] border border-line bg-surface px-3.5 text-sm text-ink shadow-card placeholder:text-subtle focus:border-accent focus:outline-none"
      />

      {(tag || service) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-subtle">
          Filtering by
          {service && <Chip label={`service:${service}`} active onClick={() => patch({ service: '' })} />}
          {tag && <Chip label={tag} active onClick={() => patch({ tag: '' })} />}
        </div>
      )}

      {data && data.facets.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-1.5">
          {data.facets.map((f) => (
            <Chip
              key={f.tag}
              label={f.tag}
              count={f.count}
              active={tag === f.tag}
              onClick={() => patch({ tag: tag === f.tag ? '' : f.tag })}
            />
          ))}
        </div>
      )}

      <div className="mt-8">
        {loading && <Spinner />}
        {error && <p className="text-sm text-danger">Search failed: {error.message}</p>}

        {data && !loading && (
          <>
            <p className="mb-4 text-xs text-subtle">
              {data.count} {data.count === 1 ? 'result' : 'results'}
            </p>

            {data.results.length === 0 ? (
              <EmptyState title="No matches">Try a different term or clear the tag filter.</EmptyState>
            ) : (
              <ul className="divide-y divide-line border-y border-line">
                {data.results.map((d) => (
                  <li key={d.id}>
                    <Link to={docPath(d)} className="block py-4 transition-colors hover:bg-sunken">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-ink">{d.title}</span>
                        <VisibilityBadge visibility={d.visibility} />
                      </div>
                      <p className="mt-0.5 line-clamp-2 text-sm text-muted">{d.description}</p>
                      <p className="mt-1.5 font-mono text-xs text-subtle">
                        {d.service} / {d.topic}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  )
}
